import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Image } from "@common/components";
import { OtpInput } from "@common/components/OtpInput";
import { env } from "@common/utils";
import {
  AuthProvider,
  useAuthenticalUrlQuery,
  useInitiateAuthenticationMutation,
  useVerifyOtpCodeMutation,
} from "@common/utils/graphql";
import { useAuthStore } from "@domains/auth/utils/useAuthStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { Body, Heading, Logo, Superscript } from "@hypercard/web-components";
import { CircleNotch } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

const schema = z.object({
  email: z
    .string({ required_error: "Please enter your email" })
    .email({ message: "Please enter a valid email" }),
  otp: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export const LoginPage = () => {
  const navigate = useNavigate();
  const [isOtp, setIsOtp] = useState(false);
  const location = useLocation();
  // path to redirect to after login, passed from AuthRedirectPage
  const from = location.state?.from;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const [initAuthentication, { loading: initAuthLoading }] =
    useInitiateAuthenticationMutation();
  const [verifyOtp, { loading: verifyOtpLoading }] = useVerifyOtpCodeMutation();
  const signOut = useAuthStore((state) => state.signOut);
  const setTokens = useAuthStore((state) => state.setTokenState);

  const { data: authUrlData } = useAuthenticalUrlQuery({
    variables: {
      redirectUri: env.authRedirectUri,
      provider: AuthProvider.GoogleOAuth,
      state: from, // adds a ?state=<from> search param to the url redirected to after authenticating
    },
  });

  const onClickSso = () => {
    const authUrl =
      authUrlData?.authenticationUrl.__typename ===
        "QueryAuthenticationUrlSuccess" && authUrlData.authenticationUrl.data;
    if (authUrl) {
      window.location.replace(authUrl);
    }
  };

  const submitOtp = useCallback(
    async ({ otp, email }: FormValues) => {
      if (!otp || otp.length != 6) {
        form.setError("otp", { message: "Invalid one-time code" });
        return;
      }
      try {
        const { data } = await verifyOtp({
          variables: {
            code: otp,
            email,
          },
        });
        if (data?.verifyOTPCode.__typename !== "MutationVerifyOTPCodeSuccess") {
          throw new Error();
        }
        const { data: otpData } = data.verifyOTPCode;
        setTokens({
          accessToken: otpData.accessToken,
          accessTokenExpiresAt: otpData.accessTokenExpiresAt,
          refreshToken: otpData.refreshToken,
          refreshTokenExpiresAt: otpData.refreshTokenExpiresAt,
        });

        // if redirect path is provided, navigate to it, otherwise navigate to home
        if (from) {
          navigate(from);
        } else {
          navigate("/home");
        }
      } catch (e) {
        form.setError("otp", {
          message: "Invalid one-time code",
        });
        console.error(e);
      }
    },
    [form, from, navigate, setTokens, verifyOtp],
  );

  const otp = form.watch("otp");

  useEffect(() => {
    if (otp?.length === 6) {
      submitOtp({ otp, email: form.getValues("email") });
    }
  }, [form, otp, submitOtp]);

  const submitEmail = async (values: FormValues) => {
    try {
      const { data } = await initAuthentication({
        variables: {
          redirectUri: env.authRedirectUri,
          email: values.email,
          state: from, // adds a ?state=<from> search param to the url redirected to after authenticating
        },
      });

      if (
        data?.initiateAuthentication.__typename !==
        "MutationInitiateAuthenticationSuccess"
      ) {
        throw new Error();
      }
      if (data.initiateAuthentication.data.authenticationUrl) {
        const { authenticationUrl } = data.initiateAuthentication.data;
        window.location.replace(authenticationUrl);
      } else {
        form.reset({ email: values.email }, { keepIsSubmitted: false });
        setIsOtp(true);
      }
    } catch (e) {
      form.setError("email", {
        message: "Invalid email address",
      });
    }
  };

  const onChangeEmail = () => {
    form.resetField("email");
    setIsOtp(false);
  };

  useEffect(() => {
    signOut();
  }, [signOut]);

  const onSubmit = isOtp ? submitOtp : submitEmail;
  const isLoading = verifyOtpLoading || initAuthLoading;

  return (
    <div className="relative h-screen w-screen">
      <video
        src={`${env.assetsUrl}/videos/hypercard-commercial.mp4`}
        className="fixed inset-0 min-h-full min-w-full object-cover"
        autoPlay
        muted
        loop
      />
      <Card className="absolute inset-4 flex flex-col border-none bg-[#121212]/70 p-10 backdrop-blur-3xl sm:inset-8 md:inset-y-8 md:left-16 md:w-[500px] md:rounded-lg">
        <Logo className="mx-auto h-6" />
        <div className="grow" />
        <Heading weight="regular" size="lg" className="text-center">
          {!isOtp ? "Sign In" : "Check Your Email"}
        </Heading>
        {isOtp && (
          <Body size="sm" weight="regular" className="mt-2 text-center">
            Enter the code sent to{" "}
            <span className="font-medium">{form.getValues("email")}</span>
          </Body>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8">
            {!isOtp ? (
              <FormField
                name="email"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel disableAsterisk={true}>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Your email address"
                        {...field}
                        onChange={(e) => {
                          onChangeEmail();
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                name="otp"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel disableAsterisk={true}>One-Time Code</FormLabel>
                    <FormControl>
                      <OtpInput {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {!isOtp && (
              <Button
                size="lg"
                type="submit"
                disabled={isLoading}
                className="mt-8 w-full"
              >
                {isLoading ? (
                  <CircleNotch className="size-4 animate-spin" />
                ) : (
                  "Continue"
                )}
              </Button>
            )}
          </form>
        </Form>

        {!isOtp && (
          <>
            <div className="mt-6 flex items-center space-x-2">
              <div className="h-px grow bg-white/10" />
              <Superscript size="md" className="text-white/70">
                OR
              </Superscript>
              <div className="h-px grow bg-white/10" />
            </div>
            <Button
              size="lg"
              className="mt-6 flex items-center justify-center gap-2"
              onClick={onClickSso}
            >
              <Image name="google-logo" className="size-4" />
              Continue with Google
            </Button>
          </>
        )}

        <div className="grow" />
        {/* offset size of logo header to center the form */}
        <div className="h-6" />
      </Card>
    </div>
  );
};
